import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Wallet, Mail, ArrowLeft, CheckCircle2, ShieldCheck } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { api } from "../lib/api";

/**
 * JMS ERP · ForgotPassword — Solicitação de link de recuperação.
 * Nunca revela se o e-mail existe (proteção contra enumeração). Backend responde
 * sempre com a mesma mensagem genérica.
 */
export default function ForgotPassword() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim().toLowerCase() });
      setSent(true);
    } catch (err) {
      // Mesmo em erro de rede, exibimos a mesma mensagem (protege enumeração).
      setSent(true);
      toast.message("Solicitação enviada");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="forgot-password-page"
      className="relative min-h-screen w-full overflow-hidden bg-[#F8FAFC] flex items-center justify-center px-4 py-8 sm:px-6"
    >
      {/* Background */}
      <div aria-hidden="true" className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 bg-[radial-gradient(60%_50%_at_50%_0%,rgba(22,163,74,0.08),transparent_65%)]" />
        <div className="absolute -top-32 -left-24 h-[360px] w-[360px] rounded-full bg-primary/15 blur-3xl opacity-70" />
        <div className="absolute top-1/3 -right-32 h-[420px] w-[420px] rounded-full bg-emerald-200/40 blur-3xl opacity-60" />
        <div className="absolute -bottom-32 left-1/3 h-[320px] w-[320px] rounded-full bg-primary/10 blur-3xl opacity-70" />
      </div>

      <div className="relative z-10 w-full max-w-[480px] fade-up">
        <div
          data-testid="forgot-password-card"
          className="bg-white rounded-[20px] shadow-[0_20px_60px_-15px_rgba(15,23,42,0.15),0_8px_20px_-8px_rgba(15,23,42,0.08)] ring-1 ring-slate-200/60 p-10"
        >
          <div className="flex flex-col items-center text-center mb-7">
            <div
              aria-hidden="true"
              className="h-14 w-14 rounded-2xl grid place-items-center bg-gradient-to-br from-primary to-emerald-600 shadow-[0_10px_25px_-8px_rgba(22,163,74,0.55)] mb-4"
            >
              <Wallet className="h-7 w-7 text-white" strokeWidth={2.2} />
            </div>
            <h1 className="font-display font-bold text-2xl tracking-tight text-slate-900">JMS ERP</h1>
            <h2 className="mt-3 font-display text-[22px] font-semibold text-slate-900 tracking-tight">
              Recuperar senha
            </h2>
            <p className="mt-2 text-sm text-slate-500 leading-relaxed max-w-[360px]">
              Informe o e-mail cadastrado e enviaremos um link seguro para redefinir sua senha.
            </p>
          </div>

          {!sent ? (
            <form onSubmit={onSubmit} className="space-y-5" data-testid="forgot-password-form">
              <div className="space-y-2">
                <Label htmlFor="fp-email" className="text-sm font-medium text-slate-700">
                  E-mail
                </Label>
                <div className="relative">
                  <Mail aria-hidden="true" className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="fp-email"
                    data-testid="forgot-password-email-input"
                    type="email"
                    autoComplete="email"
                    required
                    spellCheck={false}
                    value={email}
                    onChange={(ev) => setEmail(ev.target.value)}
                    placeholder="seu@email.com"
                    aria-label="E-mail"
                    className="h-[52px] pl-11 pr-4 rounded-xl border-slate-200 bg-slate-50/60 text-slate-900 placeholder:text-slate-400 focus-visible:bg-white focus-visible:border-primary focus-visible:ring-2 focus-visible:ring-primary/20 transition-all duration-200"
                  />
                </div>
              </div>

              <Button
                data-testid="forgot-password-submit-button"
                type="submit"
                disabled={submitting}
                className="w-full h-[54px] rounded-xl bg-primary hover:bg-primary/90 active:scale-[0.985] text-primary-foreground font-semibold text-[15px] tracking-tight shadow-[0_10px_24px_-10px_rgba(22,163,74,0.6)] hover:shadow-[0_14px_30px_-10px_rgba(22,163,74,0.7)] transition-all duration-200 disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <span className="inline-flex items-center">
                    <Loader2 className="h-4 w-4 animate-spin mr-2" aria-hidden="true" />
                    Enviando...
                  </span>
                ) : (
                  "Enviar link de recuperação"
                )}
              </Button>

              <Button
                type="button"
                variant="ghost"
                data-testid="forgot-password-back-button"
                onClick={() => navigate("/login")}
                className="w-full h-11 rounded-xl text-slate-600 hover:text-slate-900 hover:bg-slate-100 transition-colors"
              >
                <ArrowLeft className="h-4 w-4 mr-2" aria-hidden="true" />
                Voltar para o Login
              </Button>
            </form>
          ) : (
            <div className="space-y-5" data-testid="forgot-password-success">
              <div
                role="status"
                className="flex items-start gap-3 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-4 text-sm text-emerald-800"
              >
                <CheckCircle2 className="h-5 w-5 mt-0.5 shrink-0 text-emerald-600" aria-hidden="true" />
                <div className="leading-snug">
                  <strong className="font-semibold block mb-1">Solicitação recebida</strong>
                  <span data-testid="forgot-password-success-message">
                    Se o e-mail estiver cadastrado, você receberá um link para redefinir sua senha.
                  </span>
                </div>
              </div>
              <Button
                type="button"
                data-testid="forgot-password-back-to-login-button"
                onClick={() => navigate("/login")}
                className="w-full h-[54px] rounded-xl bg-primary hover:bg-primary/90 active:scale-[0.985] text-primary-foreground font-semibold text-[15px] tracking-tight shadow-[0_10px_24px_-10px_rgba(22,163,74,0.6)] transition-all duration-200"
              >
                <ArrowLeft className="h-4 w-4 mr-2" aria-hidden="true" />
                Voltar para o Login
              </Button>
              <button
                type="button"
                data-testid="forgot-password-try-another"
                onClick={() => { setSent(false); setEmail(""); }}
                className="w-full text-sm text-slate-500 hover:text-slate-700 hover:underline underline-offset-4 transition-colors"
              >
                Tentar com outro e-mail
              </button>
            </div>
          )}

          <div className="mt-8 pt-6 border-t border-slate-100 flex flex-col items-center text-center gap-1">
            <div className="inline-flex items-center gap-1.5 text-[11px] font-medium text-emerald-700">
              <ShieldCheck className="h-3.5 w-3.5" aria-hidden="true" />
              Ambiente Seguro
            </div>
            <p className="text-[11px] text-slate-400">© 2026 JMS MULTI SERVICE LTDA</p>
            <p className="text-[11px] text-slate-400">Versão 1.0</p>
          </div>
        </div>
      </div>
    </div>
  );
}
